import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';

export interface NavItem {
  to: string;
  label: string;
  icon: string;
}

const PRIMARY_NAV: NavItem[] = [
  { to: '/pos',      label: 'POS',      icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17M17 13v6a2 2 0 11-4 0v-6m4 0H9' },
  { to: '/clients',  label: 'Clients',  icon: 'M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z' },
  { to: '/payments', label: 'Payments', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
];

const MORE_NAV: NavItem[] = [
  { to: '/outstanding', label: 'Outstanding', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
  { to: '/proformas', label: 'Proformas', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { to: '/inventory', label: 'Inventory', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-14L4 7m8 4v10M4 7v10l8 4' },
  { to: '/vendors',   label: 'Vendors',   icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2m-2 0v-3m-4 3v-9m-4 9v-9' },
  { to: '/stocktake', label: 'Stocktake', icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z' },
  { to: '/email-log', label: 'Mail log', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  { to: '/settings',  label: 'Settings',  icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
];

/** All routes for desktop sidebar (primary + overflow). */
export const ALL_NAV: NavItem[] = [...PRIMARY_NAV, ...MORE_NAV];

/** Scroll clearance: capsule height + 2mm lift from screen bottom. */
export const MOBILE_NAV_CLEARANCE = 'calc(4.75rem + 2mm)';

/** Frosted-glass floating pill (iOS-style tab bar). */
const CAPSULE_CLASS =
  'grid grid-cols-4 rounded-full border border-white/70 bg-white/92 shadow-[0_4px_24px_rgba(15,23,42,0.12),0_1px_0_rgba(255,255,255,0.8)_inset] supports-[backdrop-filter]:bg-white/72 supports-[backdrop-filter]:backdrop-blur-2xl supports-[backdrop-filter]:backdrop-saturate-150';

const NAV_SHELL_CLASS =
  'md:hidden fixed inset-x-0 bottom-0 z-50 pointer-events-none px-[max(2mm,env(safe-area-inset-left,0px))] pb-[2mm]';

function NavIcon({ d, className }: { d: string; className?: string }) {
  return (
    <svg className={className ?? 'w-5 h-5'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

const TAB_BASE =
  'flex flex-col items-center justify-center w-full min-h-[52px] py-1.5 gap-0.5 text-[11px] leading-none touch-manipulation select-none';

function tabClass(isActive: boolean): string {
  return `${TAB_BASE} ${isActive ? 'text-brand-700 font-semibold' : 'text-slate-500 font-medium'}`;
}

/** Fixed 36×36 icon area so every tab lines up (including More). */
function TabIconSlot({
  children,
  active,
  rounded,
}: {
  children: ReactNode;
  active?: boolean;
  rounded?: boolean;
}) {
  return (
    <span
      className={`flex items-center justify-center w-9 h-9 shrink-0 ${
        rounded
          ? active
            ? 'rounded-full bg-brand-100/90 text-brand-700'
            : 'rounded-full bg-slate-100/80 text-slate-600'
          : ''
      }`}
    >
      {children}
    </span>
  );
}

function TabLabel({ children, active }: { children: ReactNode; active?: boolean }) {
  return (
    <span
      className={`block h-3 leading-3 truncate max-w-[4.5rem] text-center ${
        active ? 'font-semibold' : 'font-medium'
      }`}
    >
      {children}
    </span>
  );
}

export function MobileBottomNav() {
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const moreActive = MORE_NAV.some((item) => pathname === item.to || pathname.startsWith(`${item.to}/`));

  useEffect(() => {
    setMoreOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [moreOpen]);

  const ui = (
    <>
      {moreOpen && (
        <button
          type="button"
          className="md:hidden fixed inset-0 z-40 bg-slate-900/25 backdrop-blur-[2px]"
          aria-label="Close menu"
          onClick={() => setMoreOpen(false)}
        />
      )}

      {moreOpen && (
        <div
          role="dialog"
          aria-label="More navigation"
          className="md:hidden fixed inset-x-0 z-50 overflow-hidden rounded-2xl border border-white/70 bg-white/95 shadow-xl supports-[backdrop-filter]:bg-white/85 supports-[backdrop-filter]:backdrop-blur-xl mx-[max(2mm,env(safe-area-inset-left,0px))]"
          style={{ bottom: MOBILE_NAV_CLEARANCE }}
        >
          <ul className="py-1">
            {MORE_NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 text-sm touch-manipulation ${
                      isActive ? 'bg-brand-50 text-brand-700 font-medium' : 'text-slate-700 hover:bg-slate-50/80'
                    }`
                  }
                  onClick={() => setMoreOpen(false)}
                >
                  <NavIcon d={item.icon} className="w-5 h-5 shrink-0" />
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}

      <nav className={NAV_SHELL_CLASS} aria-label="Main navigation">
        <div className={`pointer-events-auto w-full ${CAPSULE_CLASS}`}>
          {PRIMARY_NAV.map((item) => (
            <NavLink key={item.to} to={item.to} className={({ isActive }) => tabClass(isActive)}>
              {({ isActive }) => (
                <>
                  <TabIconSlot>
                    <NavIcon d={item.icon} className="w-5 h-5" />
                  </TabIconSlot>
                  <TabLabel active={isActive}>{item.label}</TabLabel>
                </>
              )}
            </NavLink>
          ))}

          <button
            type="button"
            className={tabClass(moreActive || moreOpen)}
            aria-label="More menu"
            aria-expanded={moreOpen}
            aria-haspopup="dialog"
            onClick={() => setMoreOpen((v) => !v)}
          >
            <TabIconSlot active={moreActive || moreOpen} rounded>
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path strokeLinecap="round" d="M5 7h14M5 12h14M5 17h14" />
              </svg>
            </TabIconSlot>
            <TabLabel active={moreActive || moreOpen}>More</TabLabel>
          </button>
        </div>
      </nav>
    </>
  );

  return createPortal(ui, document.body);
}
